import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { Navigation } from './components/Navigation';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastContainer } from './components/Toast';
import { InstallPrompt } from './components/InstallPrompt';
import { PullToRefresh } from './components/PullToRefresh';
import { LoginPage } from './pages/LoginPage';
import { FeedPage } from './pages/FeedPage';
import { MembersPage } from './pages/MembersPage';
import ProfilePage from './pages/ProfilePage';
import EditProfilePage from './pages/EditProfilePage';
import EventsPage from './pages/EventsPage';
import CreateEventPage from './pages/CreateEventPage';
import EventDetailPage from './pages/EventDetailPage';
import EditEventPage from './pages/EditEventPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { NotificationSettingsPage } from './pages/NotificationSettingsPage';
import { MessagesPage, GroupMessagesPage } from './pages/MessagesPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminAuditLogsPage from './pages/AdminAuditLogsPage';
import AdminReleaseNotesPage from './pages/AdminReleaseNotesPage';
import PostDetailPage from './pages/PostDetailPage';
import ArchivePage from './pages/ArchivePage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function AppContent() {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  
  // Redirect to change password if user must change password
  if (isAuthenticated && user?.mustChangePassword && !location.pathname.startsWith('/change-password')) {
    return <Navigate to="/change-password" replace />;
  }
  
  // Don't show layout on login/register/change-password pages
  const showLayout = isAuthenticated && 
    !location.pathname.startsWith('/login') && 
    !location.pathname.startsWith('/register') &&
    !location.pathname.startsWith('/change-password');
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <main className="mx-auto max-w-7xl w-full px-4 py-4 sm:py-8 pb-20 lg:pb-8">
        {showLayout ? (
          <Routes>
            <Route path="/" element={<FeedPage />} />
            <Route path="/posts/:postId" element={<PostDetailPage />} />
            <Route path="/members" element={<MembersPage />} />
            <Route path="/profile/:userId" element={<ProfilePage />} />
            <Route path="/edit-profile" element={<EditProfilePage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/create" element={<CreateEventPage />} />
            <Route path="/events/:eventId" element={<EventDetailPage />} />
            <Route path="/events/:eventId/edit" element={<EditEventPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/notifications/settings" element={<NotificationSettingsPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/messages/:userId" element={<MessagesPage />} />
            <Route path="/messages/group/:conversationId" element={<GroupMessagesPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/audit-logs" element={<AdminAuditLogsPage />} />
            <Route path="/admin/release-notes" element={<AdminReleaseNotesPage />} />
            <Route path="/archive" element={<ArchivePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        ) : (
          <Routes>
            <Route path="/" element={<FeedPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        )}
      </main>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <ToastContainer />
            <InstallPrompt />
            <PullToRefresh />
            <AppContent />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
